import { Modal, ModalBody, ModalContent, ModalHeader } from '@nextui-org/react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { markdownComponents } from '../utils/markdownRenderUtils';
import { processMarkdownText } from '../utils/markdownUtils';

interface ArticleReadOnlyModalProps {
    onClose: () => void;
    articleData: Record<string, unknown>;
}

const articleTitles: Record<string, string> = {
    expert_answer: 'Expert Answer',
    knowledge_article: 'Knowledge Article',
};

// Tailwind's preflight strips heading/list defaults, so the article's markdown needs its own
// block styling on top of the shared table/list renderers.
const articleMarkdownComponents = {
    ...markdownComponents,
    h1: ({ node, ...props }: any) => <h1 className="mt-4 text-base font-semibold first:mt-0" {...props} />,
    h2: ({ node, ...props }: any) => <h2 className="mt-4 text-sm font-semibold first:mt-0" {...props} />,
    h3: ({ node, ...props }: any) => <h3 className="mt-3 text-sm font-semibold first:mt-0" {...props} />,
    h4: ({ node, ...props }: any) => <h4 className="mt-3 text-sm font-semibold first:mt-0" {...props} />,
    p: ({ node, ...props }: any) => <p className="my-2 first:mt-0 last:mb-0" {...props} />,
    ul: ({ node, ...props }: any) => <ul className="my-2 list-disc pl-5" {...props} />,
    ol: ({ node, ...props }: any) => <ol className="my-2 list-decimal pl-5" {...props} />,
    li: ({ node, ...props }: any) => <li className="my-1" {...props} />,
    a: ({ node, ...props }: any) => (
        <a className="text-primary underline" target="_blank" rel="noreferrer" {...props} />
    ),
    code: ({ node, ...props }: any) => (
        <code className="rounded bg-default-200 px-1 py-0.5 text-xs" {...props} />
    ),
    blockquote: ({ node, ...props }: any) => (
        <blockquote className="my-2 border-l-2 border-default-300 pl-3 text-default-600" {...props} />
    ),
};

const ReadOnlyContent = ({ label, value }: { label: string; value: unknown }) => {
    const text = typeof value === 'string' ? value.trim() : '';

    return (
        <section className="flex flex-col gap-1">
            <h3 className="text-xs font-medium uppercase tracking-wide text-default-400">{label}</h3>
            <div className="break-words rounded-medium border border-divider bg-content1 p-3 text-sm text-foreground">
                {text ? (
                    <ReactMarkdown remarkPlugins={[remarkGfm]} components={articleMarkdownComponents}>
                        {processMarkdownText(text)}
                    </ReactMarkdown>
                ) : (
                    '—'
                )}
            </div>
        </section>
    );
};

/** Conversation-only article viewer. It intentionally excludes authoring controls and metadata. */
const ArticleReadOnlyModal = ({ onClose, articleData }: ArticleReadOnlyModalProps) => {
    const type = String(articleData.article_type ?? '');

    return (
        <Modal
            isOpen
            onClose={onClose}
            className="max-w-[none] sm:w-[80%] md:w-[70%] lg:w-[60%]"
            scrollBehavior="inside"
        >
            <ModalContent>
                <ModalHeader>{articleTitles[type] ?? 'Article'}</ModalHeader>
                <ModalBody className="gap-4 pb-6">
                    <ReadOnlyContent label="Subject or Question" value={articleData.question} />
                    <ReadOnlyContent label="Answer" value={articleData.answer} />
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

export { ArticleReadOnlyModal };
